import { useEffect, useMemo, useState } from "react";
import { GitBranch, GitCommitHorizontal, Lock, Rocket, Tag } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Button, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, InlineAlert, Input, Textarea } from "@ncp/ui";
import type { Comparison } from "../diff/compare";
import type { GraphIssue } from "../types";

const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9._/-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "change";

export interface CommitInputs { message: string; newBranch?: string; mergeToMain: boolean }

/** Commit the canvas: a message, and on the protected main branch the branch the change goes to (optionally merged straight back). */
export function CommitDialog({ open, onOpenChange, onCommit, busy, protectedBranch, branch, comparison, issues, initialMessage }: {
  open: boolean; onOpenChange: (o: boolean) => void; onCommit: (i: CommitInputs) => void; busy: boolean; protectedBranch: boolean; branch: string; comparison: Comparison; issues: GraphIssue[]; initialMessage?: string;
}) {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [newBranch, setNewBranch] = useState("");
  const [touched, setTouched] = useState(false);
  const [merge, setMerge] = useState(false);
  useEffect(() => { if (open) { setMessage(initialMessage ?? ""); setNewBranch(""); setTouched(false); setMerge(false); } }, [open, initialMessage]);
  const branchName = touched ? newBranch : slug(message);
  const errors = issues.filter((i) => i.severity === "Error").length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitCommitHorizontal className="size-5 text-aegold-700" /> {t("admin:builder.commit.title")}</DialogTitle>
          <DialogDescription>{protectedBranch ? t("admin:builder.commit.protectedHint") : t("admin:builder.commit.hint", { branch })}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5 text-xs">
          {comparison.added ? <Badge variant="success">+{comparison.added}</Badge> : null}
          {comparison.modified ? <Badge variant="warning">~{comparison.modified}</Badge> : null}
          {comparison.removed ? <Badge variant="danger">−{comparison.removed}</Badge> : null}
          {comparison.moved ? <Badge variant="outline">{t("admin:builder.commit.moved", { count: comparison.moved })}</Badge> : null}
          {errors ? <Badge variant="danger">{t("admin:builder.commit.errors", { count: errors })}</Badge> : <Badge variant="success">{t("admin:builder.commit.valid")}</Badge>}
        </div>
        <Field label={t("admin:builder.commit.message")} required>{(id) => <Textarea id={id} rows={3} autoFocus value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("admin:builder.commit.placeholder")} dir="auto" />}</Field>
        {protectedBranch ? (
          <div className="space-y-2 rounded-xl border border-sand-200 bg-sand-50 p-3">
            <p className="flex items-center gap-1.5 text-xs font-medium"><Lock className="size-3.5" /> {t("admin:builder.commit.toBranch")}</p>
            <Input value={branchName} onChange={(e) => { setTouched(true); setNewBranch(e.target.value); }} className="font-mono text-xs" dir="ltr" />
            <label className="flex items-start gap-2 text-xs"><Checkbox checked={merge} onCheckedChange={(v) => setMerge(!!v)} disabled={errors > 0} /> <span>{t("admin:builder.commit.mergeNow")}<br /><span className="text-muted-foreground">{errors > 0 ? t("admin:builder.commit.mergeBlocked") : t("admin:builder.commit.mergeNowHint")}</span></span></label>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("actions.cancel")}</Button>
          <Button variant="gold" loading={busy} disabled={!message.trim() || (protectedBranch && !branchName.trim())} onClick={() => onCommit({ message: message.trim(), newBranch: protectedBranch ? slug(branchName) : undefined, mergeToMain: protectedBranch && merge })}>
            <GitCommitHorizontal /> {protectedBranch ? t("admin:builder.commit.commitToBranch") : t("admin:builder.commit.commit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NameDialog({ open, onOpenChange, title, description, label, initial, busy, onSubmit, icon = "branch", withNote }: {
  open: boolean; onOpenChange: (o: boolean) => void; title: string; description?: string; label: string; initial?: string; busy: boolean; onSubmit: (name: string, note: string) => void; icon?: "branch" | "tag"; withNote?: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial ?? "");
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setName(initial ?? ""); setNote(""); } }, [open, initial]);
  const Icon = icon === "tag" ? Tag : GitBranch;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Icon className="size-5 text-aegold-700" /> {title}</DialogTitle>{description ? <DialogDescription>{description}</DialogDescription> : null}</DialogHeader>
        <Field label={label} required>{(id) => <Input id={id} autoFocus value={name} onChange={(e) => setName(e.target.value)} className="font-mono" dir="ltr" onKeyDown={(e) => e.key === "Enter" && name.trim() && onSubmit(slug(name), note)} />}</Field>
        {withNote ? <Field label={t("admin:builder.dialogs.note")}>{(id) => <Input id={id} value={note} onChange={(e) => setNote(e.target.value)} dir="auto" />}</Field> : null}
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>{t("actions.cancel")}</Button><Button variant="gold" loading={busy} disabled={!name.trim()} onClick={() => onSubmit(icon === "tag" ? name.trim() : slug(name), note)}>{t("actions.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Put a commit on main into a release: approval by a second administrator makes it live for new cases. */
export function PublishDialog({ open, onOpenChange, busy, onPublish, commitLabel, issues, runningCases, liveVersion }: {
  open: boolean; onOpenChange: (o: boolean) => void; busy: boolean; onPublish: (reason: string) => void; commitLabel: string; issues: GraphIssue[]; runningCases: number; liveVersion?: number | null;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  const errors = useMemo(() => issues.filter((i) => i.severity === "Error"), [issues]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Rocket className="size-5 text-aegold-700" /> {t("admin:builder.publish.title")}</DialogTitle>
          <DialogDescription>{t("admin:builder.publish.hint", { commit: commitLabel })}</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-xs">
          <li>• {t("admin:builder.publish.fourEyes")}</li>
          <li>• {t("admin:builder.publish.running", { count: runningCases, version: liveVersion ?? "—" })}</li>
          <li>• {t("admin:builder.publish.advisor")}</li>
        </ul>
        {errors.length ? <InlineAlert tone="danger" title={t("admin:builder.publish.blocked", { count: errors.length })}><ul className="list-disc ps-4 text-xs">{errors.slice(0, 5).map((e, i) => <li key={i}>{e.message}</li>)}</ul></InlineAlert> : null}
        <Field label={t("admin:builder.publish.reason")}>{(id) => <Input id={id} value={reason} onChange={(e) => setReason(e.target.value)} dir="auto" />}</Field>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>{t("actions.cancel")}</Button><Button variant="default" loading={busy} disabled={errors.length > 0} onClick={() => onPublish(reason)}><Rocket /> {t("admin:builder.publish.stage")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
